import { TARGET_YAW_ANGLE } from "@/domain/yaw-check";

interface SideData {
  staticYaw: number;
  minYaw: number;
  maxYaw: number;
}

interface YawFoilDiagramProps {
  prt: SideData;
  stb: SideData;
}

const ANGLE_MULTIPLIER = 50;
const STICK = 88;
const PIVOT_Y = 118;
const PRT_PIVOT_X = 48;
const STB_PIVOT_X = 132;
const ARC_RADIUS = 20;

// Acceptable range: 0.15°–0.45° (±0.15 from 0.3° target)
const GREEN_THRESHOLD = 0.15;
const AMBER_THRESHOLD = 0.25;

function getColor(angle: number): string {
  const diff = Math.abs(angle - TARGET_YAW_ANGLE);
  if (diff <= GREEN_THRESHOLD) return "var(--color-green-500)";
  if (diff <= AMBER_THRESHOLD) return "var(--color-amber-500)";
  return "var(--color-red-500)";
}

function getSweepFill(angle: number): string {
  const diff = Math.abs(angle - TARGET_YAW_ANGLE);
  if (diff <= GREEN_THRESHOLD) return "rgba(34,197,94,0.08)";
  if (diff <= AMBER_THRESHOLD) return "rgba(217,119,6,0.08)";
  return "rgba(220,38,38,0.08)";
}

function getSweepStroke(angle: number): string {
  const diff = Math.abs(angle - TARGET_YAW_ANGLE);
  if (diff <= GREEN_THRESHOLD) return "rgba(34,197,94,0.2)";
  if (diff <= AMBER_THRESHOLD) return "rgba(217,119,6,0.2)";
  return "rgba(220,38,38,0.2)";
}

function toRad(deg: number, mirror: boolean): number {
  const exag = deg * ANGLE_MULTIPLIER * (Math.PI / 180);
  return mirror ? -exag : exag;
}

function stickEnd(pivotX: number, angleDeg: number, mirror: boolean) {
  const rad = toRad(angleDeg, mirror);
  return {
    x: pivotX + Math.sin(rad) * STICK,
    y: PIVOT_Y - Math.cos(rad) * STICK,
  };
}

function buildWedgePath(pivotX: number, minDeg: number, maxDeg: number, mirror: boolean): string {
  const startRad = toRad(minDeg, mirror);
  const endRad = toRad(maxDeg, mirror);
  const steps = 20;
  let path = `M ${pivotX},${PIVOT_Y} `;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const rad = startRad + (endRad - startRad) * t;
    const x = pivotX + Math.sin(rad) * STICK;
    const y = PIVOT_Y - Math.cos(rad) * STICK;
    path += `L ${x.toFixed(1)},${y.toFixed(1)} `;
  }
  return path + "Z";
}

function FoilSide({ pivotX, data, mirror }: { pivotX: number; data: SideData; mirror: boolean }) {
  const color = getColor(data.staticYaw);
  const staticEnd = stickEnd(pivotX, data.staticYaw, mirror);
  const minEnd = stickEnd(pivotX, data.minYaw, mirror);
  const maxEnd = stickEnd(pivotX, data.maxYaw, mirror);
  const targetEnd = stickEnd(pivotX, TARGET_YAW_ANGLE, mirror);
  const zeroEnd = stickEnd(pivotX, 0, mirror);
  const wedge = buildWedgePath(pivotX, data.minYaw, data.maxYaw, mirror);

  // Angle arc from zero to static
  const arcStartRad = 0;
  const arcEndRad = toRad(data.staticYaw, mirror);
  const arcStart = {
    x: pivotX + Math.sin(arcStartRad) * ARC_RADIUS,
    y: PIVOT_Y - Math.cos(arcStartRad) * ARC_RADIUS,
  };
  const arcEnd = {
    x: pivotX + Math.sin(arcEndRad) * ARC_RADIUS,
    y: PIVOT_Y - Math.cos(arcEndRad) * ARC_RADIUS,
  };
  const sweep = mirror ? 1 : 0;

  return (
    <g>
      {/* Zero reference */}
      <line
        x1={pivotX} y1={PIVOT_Y} x2={zeroEnd.x} y2={zeroEnd.y}
        className="stroke-border" strokeWidth={0.5}
      />
      {/* Sweep wedge */}
      <path d={wedge} fill={getSweepFill(data.staticYaw)} stroke={getSweepStroke(data.staticYaw)} strokeWidth={0.5} />
      {/* Min line */}
      <line
        x1={pivotX} y1={PIVOT_Y} x2={minEnd.x} y2={minEnd.y}
        stroke={color} strokeWidth={0.75} strokeDasharray="1.5,2" opacity={0.4}
      />
      {/* Max line */}
      <line
        x1={pivotX} y1={PIVOT_Y} x2={maxEnd.x} y2={maxEnd.y}
        stroke={color} strokeWidth={0.75} strokeDasharray="1.5,2" opacity={0.4}
      />
      {/* Target line */}
      <line
        x1={pivotX} y1={PIVOT_Y} x2={targetEnd.x} y2={targetEnd.y}
        className="stroke-foreground/10" strokeWidth={0.75} strokeDasharray="3,3"
      />
      {/* Static line (the foil) */}
      <line
        x1={pivotX} y1={PIVOT_Y} x2={staticEnd.x} y2={staticEnd.y}
        stroke={color} strokeWidth={2.5} strokeLinecap="round"
      />
      {/* Pivot dot */}
      <circle cx={pivotX} cy={PIVOT_Y} r={3} fill={color} opacity={0.8} />
      {/* Foil tip */}
      <circle cx={staticEnd.x} cy={staticEnd.y} r={2.5} fill={color} />
      {/* Angle arc */}
      <path
        d={`M ${arcStart.x.toFixed(1)},${arcStart.y.toFixed(1)} A ${ARC_RADIUS},${ARC_RADIUS} 0 0,${sweep} ${arcEnd.x.toFixed(1)},${arcEnd.y.toFixed(1)}`}
        fill="none" stroke={color} strokeWidth={0.6} opacity={0.5}
      />
    </g>
  );
}

export function YawFoilDiagram({ prt, stb }: YawFoilDiagramProps) {
  const allZero =
    prt.staticYaw === 0 && prt.minYaw === 0 && prt.maxYaw === 0 &&
    stb.staticYaw === 0 && stb.minYaw === 0 && stb.maxYaw === 0;

  if (allZero) return null;

  return (
    <svg
      viewBox="0 0 180 130"
      className="w-full max-w-[260px] h-auto self-center sm:w-auto sm:max-w-none sm:h-full sm:min-h-[120px] sm:max-h-[200px] sm:shrink-0 sm:self-stretch"
    >
      {/* Hull bar */}
      <rect
        x={PRT_PIVOT_X - 6} y={PIVOT_Y - 3}
        width={STB_PIVOT_X - PRT_PIVOT_X + 12} height={9}
        rx={3}
        className="fill-muted stroke-border" strokeWidth={0.5}
      />

      {/* Port foil (angles right / inward) */}
      <FoilSide pivotX={PRT_PIVOT_X} data={prt} mirror={false} />

      {/* Starboard foil (angles left / inward) */}
      <FoilSide pivotX={STB_PIVOT_X} data={stb} mirror={true} />

      {/* Side labels */}
      <text
        x={PRT_PIVOT_X - 12} y={PIVOT_Y - STICK + 12}
        className="fill-muted-foreground text-[8px] font-semibold tracking-wide"
      >
        PRT
      </text>
      <text
        x={STB_PIVOT_X + 12} y={PIVOT_Y - STICK + 12}
        textAnchor="end"
        className="fill-muted-foreground text-[8px] font-semibold tracking-wide"
      >
        STB
      </text>
    </svg>
  );
}
